<template>
  <v-card elevation="0" class="mb-4">
    <v-card-text>
      <v-row style="min-height: 450px">
        <v-col md="12">
          <div>
            <h6 class="text-h6 black--text">Items de Formula</h6>
            <p class="text-body-2">
              Registre los valores reales utilizados para cada item de la
              fórmula durante la producción
            </p>
          </div>
          <v-simple-table style="border: solid 1px #1c1c1c" >
            <template v-slot:default>
              <thead>
                <tr>
                  <th class="text-left" width="20">Orden</th>
                  <th class="text-left" width="220">Nombre</th>
                  <th class="text-left" width="300">Producto</th>
                  <th class="text-right" width="50">Cant. esperada</th>
                  <th class="text-right" width="120">Cant. utilizada</th>
                  <th class="text-left" width="80">Unidad</th>
                  <th class="text-center" width="150">Costo base</th>
                  <th class="text-center" width="150">Costo real</th>
                  <th class="text-center" width="50"></th>
                </tr>
              </thead>
              <tbody>
                <template
                  v-for="(
                    resultItem, index
                  ) in mainItem.result_formula_items"
                >
                  <tr :key="'item-' + index">
                    <td>{{ resultItem.orden_secuencia }}</td>
                    <td>
                      <div>{{ resultItem.descripcion }}</div>
                      <div class="text-caption">{{ getTypeText(resultItem.type) }}</div>
                    </td>
                    <td>
                      <v-combobox
                        v-if="resultItem.type === 'M'"
                        v-model="resultItem.mp_product_presentation_used"
                        :items="getPresentationsForItem(resultItem)"
                        :item-text="presentationLabel"
                        item-value="id"
                        dense
                        hide-details
                        class="mt-1"
                        clearable
                        :disabled="view_finish_order"
                      ></v-combobox>
                      <span v-else>-</span>
                    </td>
                    <td class="text-right">{{ resultItem.valor_base }}</td>
                    <td>
                      <v-text-field
                        v-model.number="resultItem.valor_utilizado"
                        type="number"
                        step="0.01"
                        dense
                        hide-details
                        class="mt-1"
                        :name="'valor_utilizado_' + index"
                        v-validate="'required|decimal|min_value:0'"
                        :data-vv-as="'Valor Utilizado ' + (index + 1)"
                        :error-messages="
                          errors.collect('valor_utilizado_' + index)
                        "
                        :disabled="view_finish_order"
                      ></v-text-field>
                    </td>
                    <td>{{ getStorageUnit(resultItem) }}</td>

                    <td class="text-center">$ {{ resultItem.costo_base }}</td>
                    <td>
                      <v-text-field
                        v-model.number="resultItem.costo_unitario_utilizado"
                        type="number"
                        step="0.01"
                        dense
                        hide-details
                        class="mt-1"
                        prefix="$"
                        :name="'costo_unitario_utilizado_' + index"
                        v-validate="'decimal'"
                        :data-vv-as="'Costo Unitario ' + (index + 1)"
                        :error-messages="
                          errors.collect('costo_unitario_utilizado_' + index)
                        "
                        :disabled="view_finish_order"
                      ></v-text-field>
                    </td>

                    <td class="text-center">
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on }">
                          <v-btn 
                            icon 
                            small 
                            v-on="on"
                            @click="toggleObservations(index)"
                            :color="resultItem.observacion_resultado ? 'primary' : 'grey'"
                          >
                            <v-icon small>
                              {{ expandedObservations[index] ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                            </v-icon>
                          </v-btn>
                        </template>
                        <span>{{ expandedObservations[index] ? 'Ocultar' : 'Ver' }} observaciones</span>
                      </v-tooltip>
                    </td>
                  </tr>
                  
                  <tr v-show="expandedObservations[index]" :key="'obs-' + index">
                    <td colspan="9" class="pa-3 " style="background-color: #f5f5f5;">
                      <v-textarea
                        v-model="resultItem.observacion_resultado"
                        label="Observaciones"
                        rows="2"
                        dense
                        outlined
                        :disabled="view_finish_order"
                        placeholder="Ingrese observaciones sobre este item..."
                      ></v-textarea>
                    </td>
                  </tr>
                </template>
              </tbody>
            </template>
          </v-simple-table>
        </v-col>
      </v-row>
      <div class="row justify-content-center mt-4">
        <div class="col-md-12">
          <v-btn color="primary" @click="$emit('prevStep')" small>
            Volver
          </v-btn>
          <v-btn class="float-right" color="primary" small @click="handleNext">
            Siguiente
          </v-btn>
        </div>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import { generalService } from "@/libs/ws/generalService";
import ConstantSections from "@/constants/sections";
import { FORMULA_ITEM_TYPE_CONFIG as TYPE_CONFIG } from "@/constants/formulaItemTypes";

export default {
  name: "StepFormula",
  $_veeValidate: {
    validator: "new",
  },
  props: {
    productionOrder: {
      type: Object,
      default: null,
    },
    mainItem: {
      type: Object,
      required: true,
    },
    view_finish_order: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    initialized: false,
    presentationsByRawMaterial: {},
    expandedObservations: {},
  }),
  computed: {
    hasFormula() {
      return this.productionOrder?.formula?.items;
    },
    quantityMultiplier() {
      return this.productionOrder?.quantity || 1;
    }
  },
  watch: {
    productionOrder: {
      handler: function (newValue, oldValue) {
        // Si la orden cambió (diferente ID) o si viene de null, resetear initialized
        if (newValue && (!oldValue || oldValue.id !== newValue.id)) {
          this.initialized = false;
        }
        
        if (
          newValue &&
          newValue.formula &&
          newValue.formula.items &&
          !this.initialized
        ) {
          this.initializeResultItems();
          this.loadAllPresentations();
        }
      },
      deep: true,
      immediate: true,
    },
  },
  mounted() {
    if (
      this.productionOrder &&
      this.productionOrder.formula &&
      this.productionOrder.formula.items
    ) {
      this.initializeResultItems();
      this.loadAllPresentations();
    }
  },
  methods: {
    // ========== Toggle Observaciones ==========
    toggleObservations(index) {
      this.$set(this.expandedObservations, index, !this.expandedObservations[index]);
    },
    
    // ========== Métodos Helper ==========
    getNestedProperty(obj, path) {
      return path?.split('.').reduce((acc, part) => acc?.[part], obj);
    },
    getTypeId(item) {
      return item.type?.id || item.type;
    },
    presentationLabel(item) {
      return item.label || item.name;
    },
    
    // ========== Métodos de Tipo ==========
    getTypeText(type) {
      return TYPE_CONFIG[type]?.name || type;
    },
    getDescription(item) {
      const typeId = this.getTypeId(item);
      const config = TYPE_CONFIG[typeId];
      if (!config) return "";
      
      return this.getNestedProperty(item, config.descriptionPath) || "";
    },
    getCostoBase(item) {
      const typeId = this.getTypeId(item);
      const config = TYPE_CONFIG[typeId];
      if (!config?.costPath) return 0;
      
      return parseFloat(this.getNestedProperty(item, config.costPath)) || 0;
    },
    extractStorageUnit(formula_item) {
      const typeId = this.getTypeId(formula_item);
      const config = TYPE_CONFIG[typeId];
      if (!config) return null;
      
      const entity = formula_item[config.entityKey];
      return entity?.[config.unitKey] || null;
    },
    getStorageUnit(resultItem) {
      // Para recursos, usar resource_unit
      if (resultItem.type === 'R') {
        return resultItem.resource_unit?.name || '-';
      }
      // Para otros tipos, usar storage_unit
      return resultItem.storage_unit?.name || '-';
    },
    
    // ========== Carga de Presentaciones ==========
    // ========== Carga de Presentaciones ==========
    loadAllPresentations() {
      const rawMaterialIds = this.extractRawMaterialIds();
      rawMaterialIds.forEach(id => this.loadPresentationsForRawMaterial(id));
    },
    extractRawMaterialIds() {
      const ids = new Set();
      
      if (this.mainItem.result_formula_items?.length > 0) {
        this.mainItem.result_formula_items.forEach(item => {
          if (item.type === 'M' && item.raw_material_id) {
            ids.add(item.raw_material_id);
          }
        });
      } else if (this.hasFormula) {
        this.productionOrder.formula.items.forEach(item => {
          const typeId = this.getTypeId(item);
          if (typeId === 'M' && item.raw_material?.id) {
            ids.add(item.raw_material.id);
          }
        });
      }
      
      return ids;
    },
    loadPresentationsForRawMaterial(rawMaterialId) {
      if (this.presentationsByRawMaterial[rawMaterialId]) {
        return;
      }

      const params = {
        raw_material_id: rawMaterialId,
        buyable: true
      };

      generalService.setSection(ConstantSections.SECTION_PRODUCTS);
      generalService
        .query(params, ConstantSections.SECTION_PRODUCTS + "/presentations")
        .then((response) => {
          this.$set(this.presentationsByRawMaterial, rawMaterialId, response.result);
          
          if (response.result.length === 1) {
            this.autoSelectSinglePresentation(rawMaterialId, response.result[0]);
          }
        })
        .catch((e) => {
          console.error("Error loading presentations:", e);
        });
    },
    autoSelectSinglePresentation(rawMaterialId, presentation) {
      this.mainItem.result_formula_items?.forEach((resultItem, index) => {
        if (resultItem.raw_material_id === rawMaterialId && !resultItem.mp_product_presentation_used) {
          this.$set(this.mainItem.result_formula_items[index], 'mp_product_presentation_used', presentation);
        }
      });
    },
    getPresentationsForItem(resultItem) {
      if (resultItem.type !== 'M' || !resultItem.raw_material_id) {
        return [];
      }
      return this.presentationsByRawMaterial[resultItem.raw_material_id] || [];
    },
    
    // ========== Inicialización de Items ==========
    initializeResultItems() {
      if (this.shouldInitializeFromFormula()) {
        this.createItemsFromFormula();
      } else {
        this.enrichExistingItems();
      }
      this.initialized = true;
    },
    shouldInitializeFromFormula() {
      return !this.mainItem.result_formula_items || this.mainItem.result_formula_items.length === 0;
    },
    createItemsFromFormula() {
      this.mainItem.result_formula_items = this.productionOrder.formula.items.map(
        item => this.createResultItem(item)
      );
    },
    createResultItem(formula_item) {
      const typeId = this.getTypeId(formula_item);
      const unit = this.extractStorageUnit(formula_item);
      const valorBase = formula_item.valor * this.quantityMultiplier;
      
      const baseItem = {
        formula_item_id: formula_item.id,
        orden_secuencia: formula_item.orden_secuencia,
        type: typeId,
        descripcion: this.getDescription(formula_item),
        valor_base: valorBase,
        costo_base: this.getCostoBase(formula_item),
        valor_utilizado: valorBase,
        costo_unitario_utilizado: this.getCostoBase(formula_item),
        observacion_resultado: "",
        mp_product_presentation_used: this.getInitialPresentation(formula_item, typeId),
        raw_material_id: this.getRawMaterialId(formula_item, typeId),
      };
      
      // Para Recursos usar resource_unit y resource_unit_id (NO storage_unit)
      if (typeId === 'R') {
        baseItem.resource_unit = unit;
        baseItem.resource_unit_id = unit?.id || null;
      } else {
        // Para Materias Primas, Semi-elaborados y Productos usar storage_unit y storage_unit_id (NO resource_unit)
        baseItem.storage_unit = unit;
        baseItem.storage_unit_id = unit?.id || null;
      }
      
      return baseItem;
    },
    getInitialPresentation(formula_item, typeId) {
      if (typeId === 'M') {
        return formula_item.raw_material?.product_presentation;
      }
      return undefined;
    },
    getRawMaterialId(formula_item, typeId) {
      if (typeId === 'M' && formula_item.raw_material) {
        return formula_item.raw_material.id;
      }
      return null;
    },
    enrichExistingItems() {
      this.mainItem.result_formula_items = this.mainItem.result_formula_items.map(item => {
        if (!item.formula_item) return item;
        
        const formula_item = item.formula_item;
        const typeId = this.getTypeId(formula_item);
        
        // Obtener unit de manera específica según el tipo
        let unit;
        if (typeId === 'R') {
          // Para recursos, buscar resource_unit en item o en formula_item.resource.resource_unit
          unit = item.resource_unit || formula_item.resource?.resource_unit || this.extractStorageUnit(formula_item);
        } else {
          // Para otros tipos, buscar storage_unit en item o extraer del formula_item
          unit = item.storage_unit || this.extractStorageUnit(formula_item);
        }
        
        const enrichedItem = {
          ...item,
          orden_secuencia: item.orden_secuencia || formula_item.orden_secuencia,
          type: item.type || typeId,
          descripcion: item.descripcion || this.getDescription(formula_item),
          raw_material_id: item.raw_material_id || this.getRawMaterialId(formula_item, typeId),
        };
        
        // Para Recursos: asignar SOLO resource_unit y resource_unit_id
        if (typeId === 'R') {
          enrichedItem.resource_unit = unit;
          enrichedItem.resource_unit_id = unit?.id || item.resource_unit_id || null;
          // NO incluir storage_unit ni storage_unit_id
        } else {
          // Para Materias Primas, Semi-elaborados y Productos: asignar SOLO storage_unit y storage_unit_id
          enrichedItem.storage_unit = unit;
          enrichedItem.storage_unit_id = unit?.id || item.storage_unit_id || null;
          // NO incluir resource_unit ni resource_unit_id
        }
        
        return enrichedItem;
      });
    },
    
    // ========== Validación y Guardado ==========
    async handleNext() {
      const isValid = await this.$validator.validateAll();
      if (!isValid) {
        this.$toast.error("Por favor, complete todos los campos requeridos correctamente", "Error");
        return;
      }
      
      const validation = this.validateFormData();
      if (!validation.isValid) {
        this.$toast.error(validation.message, "Error");
        return;
      }
      
      this.$emit("nextStep");
    },
    validateFormData() {      
      // Validar presentaciones de materias primas
      const hasMissingProduct = this.mainItem.result_formula_items.some(
        item => item.type === 'M' && !item.mp_product_presentation_used
      );
      if (hasMissingProduct) {
        return {
          isValid: false,
          message: "Todas las materias primas deben tener un producto seleccionado"
        };
      }
      
      return { isValid: true };
    },
  },
};
</script>
